// Cliente HTTP central. Browser → nginx (proxy) → agend-core.
// Auth: injeta Bearer JWT do localStorage no header de cada request.
// Em 401, limpa auth e redireciona pra /login.
import axios from 'axios';
import { getAuth, clearAuth } from './auth.js';

const api = axios.create({
  baseURL: '/api',
  timeout: 15000,
});

// REQ: injeta Authorization automaticamente
api.interceptors.request.use((config) => {
  const auth = getAuth();
  if (auth?.token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${auth.token}`;
  }
  return config;
});

// RESP: simplifica retorno + trata 401 (token expirado/inválido)
api.interceptors.response.use(
  (resp) => resp.data,
  (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.error || err.message;
    err.uiMessage = detail;

    // 401 em qualquer rota → desloga e redireciona (exceto se já está na /login)
    if (status === 401 && !window.location.pathname.startsWith('/login')) {
      clearAuth();
      // preserva o caminho que o usuário tentou pra voltar após login
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
    return Promise.reject(err);
  }
);

// ─── Domínio: técnicos ────────────────────────────────────────────
export const tecnicos = {
  list:        (params)   => api.get('/v1/tecnicos', { params }),
  get:         (id)       => api.get(`/v1/tecnicos/${id}`),
  agenda:      (id, data) => api.get(`/v1/tecnicos/${id}/agenda`, { params: { data } }),
  create:      (body)     => api.post('/v1/tecnicos', body),
  update:      (id, body) => api.patch(`/v1/tecnicos/${id}`, body),
  remove:      (id)       => api.delete(`/v1/tecnicos/${id}`),
  updateSkills:(id, body) => api.put(`/v1/tecnicos/${id}/skills`, body),
  recalcularCoeficiente: (id) => api.post(`/v1/tecnicos/${id}/recalcular-coeficiente`),
  setorPrimario: () => api.get('/v1/tecnicos/setor-primario'),
  setores: () => api.get('/v1/tecnicos/setores'),
};

export const clustersBairros = (clusterId) =>
  api.get(`/v1/clusters/${clusterId}/bairros`);

export const tipoOs = {
  list: () => api.get('/v1/tipos-os'),
};

// ─── Visitas ───────────────────────────────────────────────────────
export const visitas = {
  list:     (params) => api.get('/v1/visitas', { params }),
  get:      (id)     => api.get(`/v1/visitas/${id}`),
  timeline: (id)     => api.get(`/v1/visitas/${id}/timeline`),
  create:   (body)   => api.post('/v1/visitas', body),
  update:   (id,body)=> api.patch(`/v1/visitas/${id}`, body),
  trackingToken: (id)=> api.post(`/v1/visitas/${id}/tracking-token`),
  addEvento:(id,body)=> api.post(`/v1/visitas/${id}/eventos`, body),
};

// ─── Tipos de OS ───────────────────────────────────────────────────
export const tiposOs = {
  list:   (params) => api.get('/v1/tipos-os', { params }),
};

// ─── Rastreador ────────────────────────────────────────────────────
export const rastreador = {
  posicoes:  ()      => api.get('/v1/rastreador/posicoes'),
  posicao:   (id)    => api.get(`/v1/rastreador/veiculos/${id}/posicao`),
  historico: (id, h) => api.get(`/v1/rastreador/veiculos/${id}/historico`, { params: { horas: h } }),
  paradas:   (params)=> api.get('/v1/rastreador/paradas', { params }),
};

// ─── Disponibilidade ──────────────────────────────────────────────
export const disponibilidade = {
  consultar: (params) => api.get('/v1/disponibilidade', { params }),
};

// ─── Clusters ──────────────────────────────────────────────────────
export const clusters = {
  list: () => api.get('/v1/clusters'),
};

// ─── Ações IA ──────────────────────────────────────────────────────
export const acoes = {
  list:     (params)  => api.get('/v1/acoes-ia', { params }),
  get:      (id)      => api.get(`/v1/acoes-ia/${id}`),
  aprovar:  (id, body)=> api.post(`/v1/acoes-ia/${id}/aprovar`, body || {}),
  rejeitar: (id, body)=> api.post(`/v1/acoes-ia/${id}/rejeitar`, body || {}),
  tick:     ()        => api.post('/v1/acoes-ia/tick'),
  stats:    ()        => api.get('/v1/acoes-ia/stats'),
};

// ─── Auth ──────────────────────────────────────────────────────────
export const auth = {
  login: (username, senha) => api.post('/v1/auth/login', { username, senha }),
  me:    () => api.get('/v1/auth/me'),
  logout: () => api.post('/v1/auth/logout'),
};

export default api;


// ─── Escala diária / overrides de disponibilidade ────────────────
export const escala = {
  do_dia: (data) => api.get('/v1/escala', { params: { data } }),
  override: (body) => api.put('/v1/escala/override', body),
};


// ─── Feriados ─────────────────────────────────────────────────
export const feriados = {
  list:    (params)  => api.get('/v1/feriados', { params }),
  doDia:   (data)    => api.get(`/v1/feriados/data/${data}`),
  create:  (body)    => api.post('/v1/feriados', body),
  update:  (id, body) => api.patch(`/v1/feriados/${id}`, body),
  remove:  (id)      => api.delete(`/v1/feriados/${id}`),
  setPlantao: (feriadoId, tecnicoId, body) =>
    api.put(`/v1/feriados/${feriadoId}/plantao/${tecnicoId}`, body),
  removePlantao: (feriadoId, tecnicoId) =>
    api.delete(`/v1/feriados/${feriadoId}/plantao/${tecnicoId}`),
};

export const notificacoes = {
  resumo: () => api.get('/v1/notificacoes/resumo').then(r => r.data?.data || r.data),
};

export const plantoes = {
  lista:      (inicio, fim) => api.get('/v1/plantoes', { params: { inicio, fim } }).then(r => r.data?.data || r.data),
  hoje:       () => api.get('/v1/plantoes/hoje').then(r => r.data?.data || r.data),
  criar:      (body) => api.post('/v1/plantoes', body).then(r => r.data?.data || r.data),
  intervalo:  (body) => api.post('/v1/plantoes/intervalo', body).then(r => r.data),
  atualizar:  (id, body) => api.patch(`/v1/plantoes/${id}`, body).then(r => r.data?.data || r.data),
  remover:    (id) => api.delete(`/v1/plantoes/${id}`),
};

// ─── Dashboard ─────────────────────────────────────────────────────
export const dashboard = {
  snapshot: (data) => api.get('/v1/dashboard/snapshot', { params: data ? { data } : {} }),
};

// ─── Voalle API (modo híbrido) ─────────────────────────────────────
export const voalle = {
  status:    () => api.get('/v1/voalle/status'),
  historico: (limit = 20) => api.get('/v1/voalle/historico', { params: { limit } }),
};

// ─── Branding (público) ────────────────────────────────────────────
export const branding = {
  get: () => api.get('/v1/branding'),
};

// ─── Admin (apenas papel=admin) ────────────────────────────────────
export const admin = {
  // Operadores
  listarOperadores: () => api.get('/v1/admin/operadores'),
  getOperador: (id) => api.get(`/v1/admin/operadores/${id}`),
  criarOperador: (body) => api.post('/v1/admin/operadores', body),
  atualizarOperador: (id, body) => api.patch(`/v1/admin/operadores/${id}`, body),
  resetarSenha: (id) => api.post(`/v1/admin/operadores/${id}/resetar-senha`),
  desativarOperador: (id) => api.delete(`/v1/admin/operadores/${id}`),

  // Branding
  listarParametros: () => api.get('/v1/admin/parametros'),
  atualizarParametro: (chave, valor) => api.patch(`/v1/admin/parametros/${chave}`, { valor }),
  uploadLogo: (formData) => api.post('/v1/admin/branding/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  // KPIs
  kpisBackoffice: (dias = 30) => api.get('/v1/admin/kpis/backoffice', { params: { dias } }),
  kpisPontualidade: (dias = 30) => api.get('/v1/admin/kpis/pontualidade', { params: { dias } }),

  // Frota
  listarFrota: () => api.get('/v1/admin/frota'),
  tecnicosDisponiveis: () => api.get('/v1/admin/frota/tecnicos-disponiveis'),
  atualizarVeiculo: (id, body) => api.patch(`/v1/admin/frota/${id}`, body),
};

// ─── Sync status (mostra lag do datalake) ──────────────────────────
export const syncStatus = {
  get: () => api.get('/v1/sync-status'),
};

// ─── Endereço residencial do técnico ────────────────────────────
export const cep = {
  buscar: (cepNum) => api.get(`/v1/cep/${String(cepNum).replace(/\D/g, '')}`),
};

export const geocode = {
  tecnico:  (tecnicoId) => api.post(`/v1/tecnicos/${tecnicoId}/geocodar`),
  endereco: (body)      => api.post('/v1/geocoding', body),
};

// === Recomendação de agendamento (motor de IA) ===
export const agendamento = {
  sugerir: (body) => api.post('/v1/agendamento/sugerir', body),
};
